import React, { useState } from "react"

import axios from "axios"
import { useStaticQuery, graphql } from "gatsby"
import PropTypes from "prop-types"

import * as styles from "./subscribe-second.module.scss"
import FormField from "../../atoms/form-field"
import Heading from "../../atoms/heading"
import CheckboxGroup from "../checkbox-group"
import Form from "../form"

const SubscribeSecond = ({ data, changeStep, email, home, all, light }) => {
  const {
    site,
    allSanityDictionary,
    allSanityCountry,
    allSanityCity,
    allSanityConference,
  } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          siteUrl
        }
      }
      allSanityDictionary {
        nodes {
          key {
            current
          }
          value
        }
      }
      allSanityCountry {
        nodes {
          abbreviation
          name
          url
        }
      }
      allSanityCity {
        nodes {
          abbreviation
          name
          country {
            abbreviation
          }
        }
      }
      allSanityConference {
        nodes {
          name
        }
      }
    }
  `)
  const [formState, setFormState] = useState("")
  const [cities, setCities] = useState([])
  const [country, setCountry] = useState(
    allSanityCountry.nodes.find((c) => c.url === site.siteMetadata.siteUrl)
      .abbreviation
  )
  const [conference, setConference] = useState("")
  const [fields, setFields] = useState(
    data.additional_fields.map((field) => ({
      key: field.tag,
      value: "",
    }))
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    setFormState("pending")
    const customFields = []

    data.additional_fields.forEach((field) => {
      if (field.type === "country")
        customFields.push({ Key: "Country", Value: country })
      else if (field.type === "conference")
        customFields.push({ Key: "Conference", Value: conference })
      else if (field.type === "city") {
        if (cities.length === 0) {
          customFields.push({ Key: "Cities", Value: "none" })
        } else {
          cities.forEach((city) => {
            customFields.push({ Key: "Cities", Value: city })
          })
        }
      } else {
        const currentField = fields.find((f) => f.key === field.tag)
        customFields.push({ Key: currentField.key, Value: currentField.value })
      }
    })

    return axios
      .post("/.netlify/functions/createsend", {
        ListId: data.vertical.cm_id,
        Request: "Update",
        EmailAddress: email,
        CustomFields: customFields,
        ConsentToTrack: "Yes",
      })
      .then(() => {
        changeStep("third", data.vertical.slug.current)
      })
      .catch(() => {
        setFormState("error")
      })
  }

  const changeCountry = (e) => {
    setCountry(e.target.value)
    setCities([])
  }

  const changeCities = (e) => {
    let newCities = cities
    if (e.target.checked) {
      if (e.target.value === "none") {
        newCities = ["none"]
      } else {
        newCities.push(e.target.value)
        if (newCities.indexOf("none") > -1) {
          newCities.splice(newCities.indexOf("none"), 1)
        }
      }
    } else {
      newCities.splice(newCities.indexOf(e.target.value), 1)
    }
    setCities(newCities.map((x) => x))
  }

  const headerLevel = () => {
    if (home) {
      return "3"
    }
    if (all) {
      return "4"
    }
    return "1"
  }

  return (
    <>
      <Heading level={headerLevel()}>{data.heading.additional}</Heading>
      <Form
        handleSubmit={handleSubmit}
        errorMessage={
          allSanityDictionary.nodes.find(
            (item) => item.key.current === "error_message"
          )?.value
        }
        submitText={data.ctas.cta_additional}
        formState={formState}
        buttonVariant="secondary"
        className={styles.form}
      >
        {data.additional_fields.map((field) => {
          if (field.type === "city") {
            return (
              <CheckboxGroup
                label={field.name}
                htmlFor={`subscribe__${field.tag}`}
              >
                {allSanityCity.nodes.map((item) => {
                  if (country === item.country.abbreviation)
                    return (
                      <FormField
                        key={item.abbreviation}
                        label={item.name}
                        form="city"
                        field={item.abbreviation}
                        value={item.abbreviation}
                        type="checkbox"
                        changeEvent={changeCities}
                        checked={cities.includes(item.abbreviation)}
                        disabled={!country}
                      />
                    )
                  return null
                })}
                <FormField
                  label={
                    allSanityDictionary.nodes.find(
                      (item) => item.key.current === "other"
                    )?.value
                  }
                  form="city"
                  field="none"
                  value="none"
                  type="checkbox"
                  changeEvent={changeCities}
                  checked={cities.includes("none")}
                  disabled={!country}
                />
              </CheckboxGroup>
            )
          }
          if (field.type === "country") {
            return (
              <FormField
                label={field.name}
                form="subscribe"
                field={field.tag}
                value={country}
                type="select"
                changeEvent={changeCountry}
                options={allSanityCountry.nodes.map((c) => ({
                  name: c.name,
                  value: c.abbreviation,
                }))}
                required
                light={light}
              />
            )
          }
          if (field.type === "conference") {
            return (
              <FormField
                label={field.name}
                form="subscribe"
                field={field.tag}
                value={conference}
                type="select"
                changeEvent={(e) => setConference(e.target.value)}
                options={allSanityConference.nodes}
                required
                light={light}
              />
            )
          }

          return (
            <FormField
              label={field.name}
              form="subscribe"
              field={field.tag}
              type={field.type}
              options={
                field.type === "select"
                  ? field.values.map((value) => ({ value }))
                  : null
              }
              changeEvent={(e) =>
                setFields(
                  fields.map((f) => ({
                    key: f.key,
                    value: f.key === field.tag ? e.target.value : f.value,
                  }))
                )
              }
              value={fields.find((f) => f.key === field.tag).value}
              required
              light={light}
            />
          )
        })}
      </Form>
    </>
  )
}

export default SubscribeSecond

SubscribeSecond.propTypes = {
  data: PropTypes.object.isRequired,
  changeStep: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  home: PropTypes.bool,
  all: PropTypes.bool,
  light: PropTypes.bool,
}

SubscribeSecond.defaultProps = {
  home: false,
  all: false,
  light: true,
}
